import { useEffect, useRef, useState, type FormEvent } from "react";
import { Brand } from "./brand.tsx";
import { Button } from "./ui/button.tsx";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "./ui/field.tsx";
import { Input } from "./ui/input.tsx";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert.tsx";
import { type GuestEntry, type GuestEntryFailure } from "../guest-entry.ts";
import { useTheme } from "../use-theme.ts";

type EntryState =
  | { readonly kind: "ready" }
  | { readonly kind: "joining" }
  | { readonly kind: "error"; readonly reason: GuestEntryFailure };

const messages: Record<
  Exclude<GuestEntryFailure, "invalid_name" | "cancelled">,
  { title: string; description: string }
> = {
  missing_link: {
    title: "Open your guest link again",
    description:
      "Reopen the original link from your invitation. If you no longer have it, ask the person who invited you to send it again.",
  },
  invalid_link: {
    title: "This guest link is no longer available",
    description:
      "It may have expired or been revoked. Ask the person who invited you for a new link.",
  },
  site_paused: {
    title: "This site is paused",
    description: "Ask the site owner to restore it, then try again.",
  },
  rate_limited: {
    title: "Too many attempts",
    description:
      "Wait ten minutes, then try again. Keep this page open so you can reuse your link.",
  },
  unavailable: {
    title: "We couldn’t open the review",
    description:
      "Check your connection and try again. Your name is still here.",
  },
};

/** Accountless entry. The authenticated console is never mounted on this route. */
export function GuestEntryScreen({ entry }: { readonly entry: GuestEntry }) {
  useTheme();
  const [name, setName] = useState("");
  const [state, setState] = useState<EntryState>({ kind: "ready" });
  const request = useRef<AbortController | null>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  const busy = state.kind === "joining";
  const reason = !entry.hasLink
    ? "missing_link"
    : state.kind === "error"
      ? state.reason
      : null;
  const nameError = reason === "invalid_name";
  const message =
    reason !== null && reason !== "invalid_name" && reason !== "cancelled"
      ? messages[reason]
      : null;
  const canRetry = entry.hasLink && reason !== "invalid_link";

  useEffect(() => {
    document.title = "Join a review · AppNotes";
    const leave = () => {
      entry.clear();
      request.current?.abort();
    };
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted) {
        request.current = null;
        setState({ kind: "error", reason: "missing_link" });
      }
    };
    window.addEventListener("pagehide", leave);
    window.addEventListener("pageshow", restore);
    return () => {
      request.current?.abort();
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", restore);
    };
  }, [entry]);

  async function join(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (request.current !== null || busy || !canRetry) return;
    if (name.trim().length === 0 || name.trim().length > 100) {
      setState({ kind: "error", reason: "invalid_name" });
      nameInput.current?.focus();
      return;
    }
    const controller = new AbortController();
    request.current = controller;
    setState({ kind: "joining" });
    const result = await entry.exchange(name, controller.signal);
    if (controller.signal.aborted) return;
    if (!result.ok) {
      request.current = null;
      setState({ kind: "error", reason: result.reason });
      return;
    }
    try {
      window.location.assign(result.launchUrl);
    } catch {
      request.current = null;
      setState({ kind: "error", reason: "unavailable" });
    }
  }

  return (
    <div className="flex min-h-svh flex-col bg-background text-foreground">
      <header className="p-5 sm:p-8">
        <Brand />
      </header>
      <main className="flex flex-1 items-center justify-center px-5 pb-12 sm:pb-24">
        <section className="gate-card" aria-labelledby="guest-title">
          <h1 id="guest-title">Join a site review</h1>
          <p>Enter the name you want others to see. No account needed.</p>
          {message && (
            <Alert className="mt-6">
              <AlertTitle>{message.title}</AlertTitle>
              <AlertDescription>{message.description}</AlertDescription>
            </Alert>
          )}
          {canRetry && (
            <form
              className="mt-6"
              onSubmit={(event) => {
                void join(event);
              }}
              noValidate
              aria-busy={busy}
            >
              <FieldGroup className="gap-6">
                <Field data-invalid={nameError}>
                  <FieldLabel htmlFor="guest-name">Your name</FieldLabel>
                  <Input
                    ref={nameInput}
                    id="guest-name"
                    name="name"
                    autoComplete="name"
                    maxLength={100}
                    required
                    value={name}
                    readOnly={busy}
                    aria-invalid={nameError}
                    aria-describedby={
                      nameError ? "guest-name-error" : "guest-name-description"
                    }
                    onChange={(event) => setName(event.target.value)}
                  />
                  {nameError ? (
                    <FieldError id="guest-name-error">
                      Enter a name between 1 and 100 characters.
                    </FieldError>
                  ) : (
                    <FieldDescription id="guest-name-description">
                      This name appears with your comments.
                    </FieldDescription>
                  )}
                </Field>
                <Button type="submit" size="lg" aria-disabled={busy}>
                  {busy ? "Opening review…" : "Open review"}
                </Button>
              </FieldGroup>
              <span className="sr-only" role="status">
                {busy ? "Opening your review. Please wait." : ""}
              </span>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
